import classnames from 'classnames'
import omit from "omit.js"
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useContext } from 'react'
import { KeyCode, WebUI, getNid, setComponentClass, setComponentSize, prefix } from '../../wui-core/src/index'
import type { SizeType } from '../../wui-core/src/types/iCore'
import InputGroup from '../../wui-input-group/src'
import Input from '../../wui-input/src'
import { getLangInfo } from '../../wui-locale/src/tool'
import { ConfigContext, WithConfigConsumer } from '../../wui-provider/src/context'
import Tooltip from '../../wui-tooltip/src'
import InputNumberGroup from './InputNumberGroup'
import i18n from './i18n.js'
import {
    PrecisionNumber as Decimal, getMark, fixExceedNum, setCaretPosition, value4calc, convertToChineseCurrency, getFormat, isThousandth, isEqual,
    numToFixed, changeMinusRightPosition, getPrecision, stateHasDiff, value4show, deleteSpaces, getFullNum, decodeValue, delcommafy, detail, isIE, judgeValue
} from './utils'
import type { InputNumberDefaultProps, InputNumberProps, InputNumberState } from './iInputNumber'

// 只读规范更改：不支持onFocus事件、hover和选中时没有额外视觉效果、无法通过tab按键选中
const defaultProps: InputNumberDefaultProps = {
    size: 'md',
    value: '',
    step: 1,
    iconStyle: 'double',
    autoFix: false,
    autoWidth: false,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    readOnly: false,
    delay: 300,
    autoFocus: false,
    disabled: false,
    locale: 'zh-cn',
    toNumber: true,
    displayCheckPrompt: false,
    keyboard: true,
    rootClassName: '',
    autoCorrectCase: false,
    roundAt: 5,
    selectOnFocus: false
}

const InputNumberFunc: React.FC<InputNumberProps> = (props) => {
    const context = useContext(ConfigContext)

    const inputRef = useRef<any>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const prevPropsRef = useRef<InputNumberProps>()
    const initData = judgeValue({ isInit: true, props: props })
    const initFormat = getFormat(props)

    const [focus, setFocus] = useState<boolean>(false)
    const [thousandSeparator, setThousandSeparator] = useState(initFormat.thousandSeparator)
    const [decimalSeparator, setDecimalSeparator] = useState(initFormat.decimalSeparator)
    const [minusRight, setMinusRight] = useState(initFormat.minusRight)
    const [toThousands, setToThousands] = useState(initFormat.toThousands)
    const [precision, setPrecision] = useState(props.precision)
    const [value, setValue] = useState(initData.value)
    const [max, setMax] = useState(props.max === 0 ? 0 : props.max || Number.MAX_SAFE_INTEGER || Math.pow(2, 53) - 1)
    const [min, setMin] = useState(props.min === 0 ? 0 : props.min || Number.MIN_SAFE_INTEGER || (Math.pow(2, 53) - 1) * -1)
    const [minusDisabled, setMinusDisabled] = useState(initData.minusDisabled)
    const [plusDisabled, setPlusDisabled] = useState(initData.plusDisabled)
    const [showValue, setShowValue] = useState(value4show(initData.value, initFormat.thousandSeparator, initFormat.decimalSeparator, initFormat.minusRight))
    const [placeholderShow, setPlaceholderShow] = useState(true)
    const [preValue, setPreValue] = useState<string | number>('') // 输入的合法的数据, onChange事件触发此值的修改更新

    // 为了兼容性，创建一个state对象供后续使用
    const state = useMemo(() => ({
        thousandSeparator,
        decimalSeparator,
        minusRight,
        toThousands,
        precision,
        value,
        max,
        min,
        minusDisabled,
        plusDisabled,
        showValue,
        placeholderShow,
        preValue,
        local: getLangInfo(props.locale, i18n, 'inputNumber')
    }), [thousandSeparator, decimalSeparator, minusRight, toThousands, precision, value, max, min, minusDisabled, plusDisabled, showValue, placeholderShow, preValue, props.locale])

    // componentDidMount
    useEffect(() => {
        if (props.autoFocus) inputRef.current?.input?.focus()
    }, [])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    // componentWillReceiveProps - 处理props变化：改为 useLayoutEffect，匹配类组件的展示时机（绘制前更新）
    useLayoutEffect(() => {
        // 跳过首次渲染，模拟 componentWillReceiveProps 的行为
        // 使用 prevPropsRef 来确保只在 props 真正变化时执行
        if (!prevPropsRef.current) {
            prevPropsRef.current = props
            return
        }
        const prevProps = prevPropsRef.current
        prevPropsRef.current = props
        const { max, min, step, value, precision } = props
        if (
            prevProps.max === max &&
            prevProps.min === min &&
            prevProps.step === step &&
            prevProps.value === value &&
            prevProps.precision === precision &&
            prevProps.thousandSeparator === props.thousandSeparator &&
            prevProps.decimalSeparator === props.decimalSeparator &&
            prevProps.toThousands === props.toThousands &&
            prevProps.minusRight === props.minusRight
        ) {
            return
        }
        const { thousandSeparator, decimalSeparator, minusRight, toThousands } = getFormat(props)

        // 与类版保持一致：只要有值（非 undefined）就设置，不比较当前 state
        if (thousandSeparator !== undefined) setThousandSeparator(thousandSeparator)
        if (decimalSeparator !== undefined) setDecimalSeparator(decimalSeparator)

        if (max !== undefined && max !== state.max) {
            setMax(max)
            setPlusDisabled(max !== undefined && (+state.value >= max || (step !== undefined && +state.value + step > max)))
        }

        if (min !== undefined && min !== state.min) {
            setMin(min)
            setMinusDisabled(min !== undefined && (+state.value <= min || (step !== undefined && +state.value - +step < min)))
        }

        if (toThousands !== undefined && toThousands !== state.toThousands) {
            let data = judgeValue({ state, props: props, oldValue: state.value, precision })
            setToThousands(toThousands)
            setShowValue(value4show(data.value, thousandSeparator, decimalSeparator, minusRight))
        }

        if (minusRight !== undefined && minusRight !== state.minusRight) {
            let data = judgeValue({ state, props: props, oldValue: state.value, precision })
            setMinusRight(minusRight)
            setShowValue(value4show(data.value, thousandSeparator, decimalSeparator, minusRight))
        }

        if (precision !== undefined && precision !== state.precision) {
            let data = judgeValue({ state, props: props, oldValue: state.value, precision })
            setPrecision(precision)
            setValue(data.value)
            setPreValue(data.value)
            setShowValue(value4show(data.value, thousandSeparator, decimalSeparator, minusRight))
        }

        // 处理value变化 - 最复杂的逻辑，完全保持原有逻辑
        if (value !== state.value) {
            if (focus) {
                if (value !== Infinity && value !== -Infinity) {
                    let data = judgeValue({ state, props: props, oldValue: state.value })
                    let nextValue = deleteSpaces(value ?? '')
                    let isThousand = isThousandth({ value: nextValue, thousandSeparator: '', decimalSeparator: '.' })
                    if (isThousand) {
                        nextValue = delcommafy({ num: nextValue, thousandSeparator })
                    }
                    // 处理focus时value传入不符合精度的数值，显示错误的问题
                    const precisedValue = getPrecision(props, nextValue, precision);
                    if (precision !== undefined && Number(nextValue) !== Number(precisedValue)) {
                        nextValue = precisedValue;
                    }
                    const newState = {
                        value: nextValue,
                        preValue: nextValue,
                        showValue: value4show(nextValue, thousandSeparator, decimalSeparator, minusRight),
                        minusDisabled: data.minusDisabled,
                        plusDisabled: data.plusDisabled
                    }
                    // 只有newState有不同的值才更新state, 避免多余setState
                    if (stateHasDiff(newState, state)) {
                        setValue(newState.value)
                        setPreValue(newState.preValue)
                        setShowValue(newState.showValue)
                        setMinusDisabled(newState.minusDisabled)
                        setPlusDisabled(newState.plusDisabled)
                    }
                }
            } else {
                let data = judgeValue({ state, props: props, oldValue: state.value })
                const newState = {
                    value: data.value,
                    preValue: data.value,
                    showValue: value4show(data.value, thousandSeparator, decimalSeparator, minusRight),
                    minusDisabled: data.minusDisabled,
                    plusDisabled: data.plusDisabled
                }
                if (stateHasDiff(newState, state)) {
                    setValue(newState.value)
                    setPreValue(newState.preValue)
                    setShowValue(newState.showValue)
                    setMinusDisabled(newState.minusDisabled)
                    setPlusDisabled(newState.plusDisabled)
                }
            }
        }
    }, [props])

    // 清理定时器
    const clear = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
    }, [])

    // handleChange - 保持原有的复杂逻辑完全不变
    const handleChange = (value: string | number, e: React.MouseEvent<HTMLInputElement>) => {
        const { onChange, toNumber, antd, min, max, step, autoFix, round } = props
        const { precision, toThousands, thousandSeparator, decimalSeparator, minusRight } = state

        let selectionStart = inputRef.current?.selectionStart === undefined
            ? inputRef.current?.input?.selectionStart
            : inputRef.current.selectionStart

        if (antd) {
            /** antd模式下value与e顺序对调 */
            e = value as unknown as React.MouseEvent<HTMLInputElement>
            value = (e as any).target.value as string
        }

        const originalValue = value;
        value = deleteSpaces(value as string)

        if (inputRef.current?.isComposition === true) {
            inputRef.current.blur()
            inputRef.current.focus()
            return
        }

        value = decimalSeparator === '.' ? value.replace(/。/g, '.') : value
        value = decimalSeparator === ',' ? value.replace(/，/g, ',') : value

        const removeDotValue = value.split(decimalSeparator)
        if (removeDotValue.length > 2) {
            value = removeDotValue[0] + decimalSeparator + removeDotValue.slice(1).join('')
        }

        const reg = new RegExp(`[^e+-\\d${thousandSeparator === '.' ? '\\.' : thousandSeparator}${decimalSeparator === '.' ? '\\.' : decimalSeparator}]+`, 'ig')
        if (reg.test(value.slice(-1))) return

        if (['-', decimalSeparator].includes(value)) {
            setValue(value)
            setPreValue(value)
            setShowValue(value)
            return
        }

        if (value === '') {
            onChange?.(value, e)
            setValue(value)
            setPreValue('')
            setShowValue('')
            return
        }

        if (minusRight) if ((value.match(/-/g)?.length ?? 0) > 1) return

        if (toThousands) {
            const _reg = new RegExp(thousandSeparator === '.' ? '\\.' : thousandSeparator, 'g')
            value = value.replace(_reg, '')
        }

        value = value4calc({ value, thousandSeparator, decimalSeparator })

        if (isNaN(+value) && value !== decimalSeparator && value !== '-' && !isThousandth({ value, thousandSeparator, decimalSeparator })) {
            setValue(value)
            setShowValue(value4show(value, thousandSeparator, decimalSeparator, minusRight))
            setPreValue(minusRight && String(value).includes('-') ? String(value).replace('-', '') + '-' : value)
            if (state.value && value.replace('' + state.value, '') === '-') {
                onChange?.(value, e)
            }
            return
        }

        if (value.indexOf(decimalSeparator) !== -1) {
            let prec = String(value.split(decimalSeparator)[1]).replace('-', '')
            if (precision === 0 && (prec === '' || prec != '')) return
            if (precision && prec.length > precision) value = numToFixed(decimalSeparator, value, precision, round)
        }

        if (autoFix && /\d/.test(value[value.length - 1])) {
            const { value: _value } = fixExceedNum(value, props, getLangInfo(props.locale, i18n, 'inputNumber'), { thousandSeparator, decimalSeparator, minusRight, max, min })
            if (min === undefined || max === undefined) {
                value = _value
            } else {
                if (
                    (max <= 0 && +value < max) ||
                    (min >= 0 && +value > min) ||
                    (max * min < 0 && (+value < min || +value > max))
                ) {
                    value = _value
                }
            }
        }

        let plusDisabled = max !== undefined && (+value >= max || +value + +step! > max),
            minusDisabled = min !== undefined && (+value <= min || +value - step! < min)
        const _showValue = value4show(value, thousandSeparator, decimalSeparator, minusRight)

        const fixCaretPosition = () => {
            if (toThousands) {
                let position = selectionStart;
                let _reg = /[0-9]/;
                for (let i = 0; i < selectionStart; i++) {
                    if (!_reg.test(_showValue[i])) position += 1;
                    if ((originalValue as string)[i] !== undefined && !_reg.test((originalValue as string)[i])) position -= 1;
                }
                setCaretPosition(inputRef.current?.input, position, true)
            }
        }

        setValue(value)
        setShowValue(_showValue)
        setPreValue(value)
        setPlusDisabled(plusDisabled)
        setMinusDisabled(minusDisabled)

        // 使用 useLayoutEffect 的方式立即执行，更接近 setState 回调的行为
        requestAnimationFrame(fixCaretPosition)

        if (value === '-') {
            onChange?.(value, e)
        } else if (value == '.' || value.indexOf('.') == value.length - 1) {
            onChange?.(value, e)
        } else if (value !== '0' && value[value.indexOf('.') + 1] === '0') {
            onChange?.(value, e)
        } else {
            toNumber ? onChange?.(value ? Number(value) : '', e) : onChange?.(value, e)
        }
    }

    // handleFocus
    const handleFocus = (_value: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (props.readOnly) return
        setFocus(true)
        const { onFocus, precision, toNumber, selectOnFocus } = props
        const { minusRight } = state

        if (selectOnFocus) {
            // 使用 setTimeout 确保在 focus 事件完成后再执行 select
            setTimeout(() => {
                inputRef.current?.input?.select()
            }, 0)
        }

        if (onFocus) {
            let value = getPrecision(props, state.value, precision)
            if (toNumber) {
                if (minusRight) {
                    value = '' + changeMinusRightPosition(value, minusRight)
                }
                onFocus?.(value ? Number(value) : '', e)
            } else {
                onFocus?.(value, e)
            }
        } else {
            inputRef.current?.input?.focus()
        }
    }

    // handleBlur - 保持原有逻辑
    const handleBlur = (v: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (props.readOnly) return
        setFocus(false)
        const { onBlur, onChange, toNumber, changeOnBlur = true, decimalFormat, dir } = props
        const { thousandSeparator, decimalSeparator, minusRight } = state

        v = isEqual(state).toString()

        if (['-', decimalSeparator].includes(v)) {
            setValue('')
            setShowValue('')
            onChange?.('')
            onBlur?.('', e)
            return
        }

        if (v === '' || !v) {
            setValue(v)
            setShowValue(value4show(v, thousandSeparator, decimalSeparator, minusRight))
            onChange?.(v)
            onBlur?.(v, e)
            return
        }

        if (v && v.match(/e/g) && v.match(/e/g)!.length > 1) {
            v = v.replace(/e/g, '')
        }
        if (v && v.match(/\+/g) && v.match(/\+/g)!.length > 0) {
            v = v.indexOf('+') !== 0 ? v.replace(/\+/g, '') : ('+' + v.replace(/\+/g, ''));
        }
        if ((minusRight || dir === "rtl") && String(v).indexOf('-') !== -1) {
            v = String(v).replace('-', '') + '-'
        }
        if (v.indexOf('-') !== -1 && !['-'].includes(v)) {
            let strValue = '';
            for (let i = 0; i < v.length; i++) {
                if (v[i] !== '-') {
                    strValue += v[i]
                } else if ((i === v.length - 1 && (minusRight || dir === "rtl")) || i === 0 || (i !== 0 && i !== v.length - 1 && v[i - 1] === 'e' && /^\d$/.test(v[i + 1]))) {
                    strValue += v[i]
                }
            }
            v = strValue;
        }

        let { value, showValue } = fixExceedNum(v, props, getLangInfo(props.locale, i18n, 'inputNumber'), { thousandSeparator, decimalSeparator, minusRight, max, min })
        if (decimalFormat && value.indexOf('.') !== -1 && value[value.length - 1] === '0') {
            let tempShowValue = showValue.split('.');
            value = Number(value) + '';
            let tempValue = value.split('.');
            showValue = tempValue.length > 1 ? (tempShowValue[0] + '.' + tempValue[1]) : tempShowValue[0]
        }

        setValue(value)
        setShowValue(showValue)
        setPlaceholderShow(true)
        detailDisable(value)

        if (toNumber) {
            if (minusRight) {
                value = '' + changeMinusRightPosition(value, minusRight)
            }
            changeOnBlur && onChange?.(Number(value))
            onBlur?.(value ? Number(value) : '', e)
        } else {
            changeOnBlur && onChange?.(value, e)
            onBlur?.(value, e)
        }
    }

    const detailDisable = (value: string | number) => {
        let { max, min, step } = props
        if (!max && max !== 0) max = state.max
        if (!min && min !== 0) min = state.min

        setPlusDisabled(value === '' || (max !== undefined && (+value >= max || +value + +step! > max)))
        setMinusDisabled(value === '' || (min !== undefined && (+value <= min || +value - step! < min)))
    }

    const handleStepChange = (value: number | InstanceType<typeof Decimal>, e: React.MouseEvent<HTMLElement>, operation: 'add' | 'reduce') => {
        let { min, max, step, onChange, toNumber } = props
        const { thousandSeparator, decimalSeparator, minusRight } = state

        if (!max && max !== 0) max = state.max
        if (!min && min !== 0) min = state.min

        if (operation === 'reduce') {
            if (typeof min === 'undefined') {
                value = value instanceof Decimal ? detail(props, state, String(value), step, 'reduce') : +detail(props, state, value, step, 'reduce')
            } else {
                if ((min || min === 0) && (value instanceof Decimal ? value.lessThan(+min) : value < +min)) {
                    value = +min
                } else {
                    let reducedValue = value instanceof Decimal ? detail(props, state, String(value), step, 'reduce') : +detail(props, state, value, step, 'reduce')
                    if (+reducedValue >= +min) {
                        value = reducedValue
                    }
                }
            }
            if (max || max === 0) {
                max = Number(max)
                if (value instanceof Decimal ? value.greaterThan(max) : value > max) {
                    value = max
                }
            }
        } else {
            if (typeof max === 'undefined') {
                value = value instanceof Decimal ? detail(props, state, String(value), step, 'add') : +detail(props, state, value, step, 'add')
            } else {
                if ((max || max === 0) && (value instanceof Decimal ? value.greaterThan(+max) : value > +max)) {
                    value = +max
                } else {
                    let addedValue = value instanceof Decimal ? detail(props, state, String(value), step, 'add') : +detail(props, state, value, step, 'add')
                    if ((max || max === 0) && addedValue <= +max) {
                        value = addedValue
                    }
                }
            }
            if (min || min === 0) {
                min = Number(min)
                if (value instanceof Decimal ? value.lessThan(min) : value < min) {
                    value = min
                }
            }
        }

        let valueFinal = value instanceof Decimal ? String(value) : value

        setValue(valueFinal)
        setPreValue(valueFinal)
        setShowValue(value4show(valueFinal, thousandSeparator, decimalSeparator, minusRight))

        // 使用 requestAnimationFrame 更接近 setState 回调的行为
        requestAnimationFrame(() => {
            inputRef.current?.input?.focus()
        })

        toNumber ? onChange?.((value || value === 0) ? Number(value) : '', e) : onChange?.(valueFinal, e)
        handleBtnClick(operation === 'add' ? 'up' : 'down', valueFinal)
        detailDisable(valueFinal)
    }

    // 使用 ref 保存最新的 state 和 props，避免闭包问题
    const stateRef = useRef(state)
    const propsRef = useRef(props)
    stateRef.current = state
    propsRef.current = props

    const handleStepMouseDown = (e: React.MouseEvent<HTMLDivElement>, operation: 'add' | 'reduce') => {
        e.persist()
        e.nativeEvent.stopPropagation()
        e.nativeEvent.preventDefault()

        const { delay, disabled, readOnly } = propsRef.current
        let { value, minusRight } = stateRef.current

        if (disabled || readOnly) return

        value = isEqual(stateRef.current)
        value = changeMinusRightPosition(value, minusRight)

        let valueFinal = isNaN(parseFloat(value.toString())) ? 0 : (propsRef.current.toNumber ? parseFloat(value.toString()) : new Decimal(value.toString()))

        handleStepChange(valueFinal, e, operation)
        clear()

        timerRef.current = setTimeout(() => {
            handleStepMouseDown(e, operation)
        }, delay)
    }

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        const { which } = e
        const { keyboard, onPressEnter, onKeyDown } = props

        if (which === KeyCode.ENTER) {
            clear()
            onPressEnter?.(e)
        }

        if (keyboard === true) {
            if (KeyCode.UP === which) {
                handleStepMouseDown(e as any, 'add')
                e.preventDefault()
            } else if (KeyCode.DOWN === which) {
                handleStepMouseDown(e as any, 'reduce')
                e.preventDefault()
            }
        }

        onKeyDown?.(e)
    }

    const onKeyUp = () => clear()

    const handleBtnClick = (type: 'up' | 'down', value: number | string) => {
        const { onStep, handleBtnClick, step } = props
        handleBtnClick?.(type, value)
        setFocus(true)
        onStep?.(value, { offset: step!, type })
    }

    const placeholderClick = () => {
        inputRef.current?.input.focus()
        setPlaceholderShow(false)
    }

    const handleComposition = (e: React.CompositionEvent<HTMLInputElement>, composition: 'start' | 'end') => {
        const { onCompositionStart, onCompositionEnd } = props

        if (composition === 'start') {
            inputRef.current.isComposition = true
            onCompositionStart?.(e)
        } else if (composition === 'end') {
            inputRef.current.isComposition = false
            onCompositionEnd?.(e)
        }
    }

    const getInput = ({ others, size, value, mark }: { others: InputNumberProps, size: InputNumberProps['size'], value: InputNumberState['value'], mark?: string }) => {
        const { id, fieldid, disabled, readOnly, clsPrefix, ignoreStopKey } = props

        const inputDom = <Input
            {...omit(others, ['nid', 'uitype', 'onResize', 'onMouseEnter', 'onMouseLeave', 'onLinkClick'])}
            prefix={!focus && mark ? <span className={`${clsPrefix}-mark`}>{mark}</span> : null}
            id={id}
            ignoreStopKey={ignoreStopKey}
            fieldid={fieldid}
            size={size}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleChange}
            onCompositionStart={e => {
                handleComposition(e, 'start')
            }}
            onCompositionEnd={e => {
                handleComposition(e, 'end')
            }}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            ref={inputRef}
        />
        return inputDom
    }
    const getMarkInput = ({ others, size, value, value1 }: { others: InputNumberProps, size: InputNumberProps['size'], value: InputNumberState['value'], value1: InputNumberState['value'] }) => {
        const { showMark, iconStyle } = props
        const mark = getMark(props, state, value, getLangInfo(props.locale, i18n, 'inputNumber'))
        value = value1 || value

        if (showMark) {
            return <Tooltip overlay={mark} placement={iconStyle === 'one' ? "topLeft" : 'top'} show={!!mark && focus} trigger={['click', 'focus']} overlayInnerStyle={{ minWidth: 20, padding: '2px 6px 0' }}>
                {getInput({ others, size, value, mark })}
            </Tooltip>
        } else {
            return getInput({ others, size, value })
        }
    }

    const browserInput = ({ toThousands, showValue, value, amount, bordered }: { toThousands: boolean, showValue: string, value: any, amount: any, bordered: string | boolean }) => {
        const { onlyShowAmount, addonBefore, addonAfter, showAmountLayout = 'vertical', clsPrefix, browserClassName, browserStyle, returnBrowserValue, showMark, onLinkClick } = props
        let renderValue: any = toThousands ? showValue : value
        const isOnlyShowAmount = onlyShowAmount ? '' : '大写：'
        const titleValue = (addonBefore && typeof addonBefore === 'string' ? addonBefore : '') + renderValue + (addonAfter && typeof addonAfter === 'string' ? addonAfter : '')
        const title = renderValue ? (amount ? titleValue + ' ' + isOnlyShowAmount + amount : titleValue) : undefined

        if (showMark) {
            renderValue = (
                <>
                    <span className={`${clsPrefix}-browser-mark`}>{getMark(props, state, renderValue, getLangInfo(props.locale, i18n, 'inputNumber'))}</span>
                    {renderValue}
                </>
            )
        }

        if (returnBrowserValue) {
            return <>
                {addonBefore}
                {renderValue}
                {addonAfter}
            </>
        }
        // 判断是否需要显示为可点击状态
        const isClickable = onLinkClick && renderValue
        const handleTriggerClick = () => {
            if (isClickable && onLinkClick) {
                onLinkClick(state.value)
            }
        }

        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
            [`${clsPrefix}-browser-wrapper-vertical`]: showAmountLayout === 'vertical' && amount,
            [`${prefix}-component-browser-${size}`]: !!size
        })

        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${clsPrefix}-browser-amount-horizontal`]: showAmountLayout === 'horizontal' && amount,
            [`${clsPrefix}-browser-amount-vertical`]: showAmountLayout === 'vertical' && amount,
            [`${browserClassName}`]: browserClassName,
            [`${clsPrefix}-browser-clickable`]: isClickable
        })

        const contentCls = classnames(`${clsPrefix}-browser-content`, {
            [`${prefix}-component-browser-${size}`]: !!size && amount && showAmountLayout === 'vertical'
        })

        const innerInput = () => {
            const tag = renderValue ? (
                <>
                    {addonBefore}
                    {renderValue}
                    {addonAfter}
                </>
            ) : "--"
            if (amount && showAmountLayout === 'vertical') {
                return (
                    <>
                        <div className={contentCls}>
                            <div className={`${clsPrefix}-browser-inner`}>{tag}</div>
                        </div>
                        <div className={`${clsPrefix}-browser-amount`}>{isOnlyShowAmount}{amount}</div>
                    </>
                )
            } else {
                return (
                    <div className={`${clsPrefix}-browser-inner`}>
                        {tag}
                        {amount && showAmountLayout === 'horizontal' && <span className={`${clsPrefix}-browser-amount`}>{isOnlyShowAmount}{amount}</span>}
                    </div>
                )
            }
        }
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle} title={title} fieldid={fieldid ? fieldid : undefined} onClick={handleTriggerClick}>
                    {innerInput()}
                </div>
            </div>
        )
    }

    const domInput = (browser?: boolean) => {
        let {
            id, fieldid, toThousands: _toThousands, minusRight: _minusRight, precision, step, disabled, readOnly,
            clsPrefix, className, delay, onCompositionStart, onCompositionEnd, onBlur, onFocus, onKeyDown: onKeyDownProp,
            onKeyUp: onKeyUpProp, iconStyle, autoWidth, onChange, format, toNumber, hideActionButton, controls,
            handleBtnClick: handleBtnClickProp, onStep, addonBefore, addonAfter, displayCheckPrompt, onPressEnter,
            formatter, style, rootClassName, autoFix, round, size, requiredStyle, align, bordered, onResize,
            decimalSeparator: decimalSeparatorProp, dir, showAmountInWords, inputWidth, onlyShowAmount, formatReg,
            hiddenChart, replaceChart, browserClassName, browserStyle, roundAt = 5, ...others
        } = props

        // ConfigProvider的context配置
        bordered = bordered ?? context?.bordered ?? true
        hideActionButton = hideActionButton ?? controls ?? false

        let classes = {
            [`${clsPrefix}-auto`]: autoWidth,
            [`${clsPrefix}`]: true,
            [`${clsPrefix}-rtl`]: dir === 'rtl',
            'disabled-con': disabled,
            [`${clsPrefix}-readonly-con`]: readOnly
        }

        let addonClasses = {
            [`${clsPrefix}-addon-${setComponentSize(size)}`]: setComponentSize(size)
        }

        size = setComponentSize(size, { defaultIsMd: true }) as SizeType
        let { value, minusDisabled, plusDisabled, showValue, toThousands, minusRight } = state
        value = precision != null && !focus ? getPrecision(props, value, precision) : value

        if (!format && formatter) format = formatter
        value = String(value).indexOf('e') !== -1 ? getFullNum({ roundAt, value, decimalSeparator: state.decimalSeparator, precision }) : value

        if ((minusRight || dir === "rtl") && String(value).indexOf('-') !== -1) {
            value = String(value).replace('-', '') + '-'
        }

        value = String(value).replace('.', state.decimalSeparator)
        value = format && !focus ? format(state.value) : value

        const plusClass = classnames('plus', {
            disabled: plusDisabled,
            'disabled-cursor': disabled,
            readOnly: plusDisabled,
            [`${clsPrefix}-readonly-cursor`]: readOnly
        })

        const minusClass = classnames('reduce', {
            disabled: minusDisabled,
            'disabled-cursor': disabled,
            readOnly: minusDisabled,
            [`${clsPrefix}-readonly-cursor`]: readOnly
        })

        // 金额大写功能只在中文环境下生效
        let tag = showAmountInWords && getLangInfo(props.locale, i18n, 'inputNumber').lang === 'zh-cn'
        let amount = tag && convertToChineseCurrency(state.value)
        value = formatReg && !focus ? decodeValue(value, formatReg, hiddenChart, replaceChart) : value
        showValue = formatReg && !focus ? decodeValue(showValue, formatReg, hiddenChart, replaceChart) : showValue
        if (browser) {
            return browserInput({ toThousands, showValue, value, amount, bordered })
        }

        return (
            <>
                {addonBefore ? (
                    <div className={classnames(className && `${className}-addonBefore`, `${clsPrefix}-addonBefore`, addonClasses)}>
                        {addonBefore}
                    </div>
                ) : null}
                {iconStyle === 'double' ? (
                    <InputGroup className={classnames(className, classes, `${clsPrefix}-group`)}>
                        {hideActionButton ? (
                            ''
                        ) : (
                            <InputGroup.Addon
                                id={id ? id + '_minus' : undefined}
                                fieldid={fieldid ? fieldid + '_minus' : undefined}
                                className={minusClass}
                                onMouseDown={(e: any) => handleStepMouseDown(e, 'reduce')}
                                onClick={(e: MouseEvent) => e.stopPropagation()}
                                onMouseLeave={() => clear()}
                                onMouseUp={() => clear()}
                            >
                                -
                            </InputGroup.Addon>
                        )}
                        {getMarkInput({ others, size, value: toThousands ? showValue : value, value1: format ? value : '' })}
                        {hideActionButton ? (
                            ''
                        ) : (
                            <InputGroup.Addon
                                id={id ? id + '_plus' : undefined}
                                fieldid={fieldid ? fieldid + '_plus' : undefined}
                                className={plusClass}
                                onMouseDown={(e: any) => handleStepMouseDown(e, 'add')}
                                onClick={(e: MouseEvent) => e.stopPropagation()}
                                onMouseLeave={() => clear()}
                                onMouseUp={() => clear()}
                            >
                                +
                            </InputGroup.Addon>
                        )}
                    </InputGroup>
                ) : (
                    <InputGroup className={classnames(className, classes, `${clsPrefix}-group`)} simple>
                        {isIE() && !value ? (
                            <div
                                onClick={placeholderClick}
                                style={{
                                    display: state.placeholderShow ? 'block' : 'none'
                                }}
                                className={`${clsPrefix}-placeholder`}
                            >
                                {props.placeholder}
                            </div>
                        ) : (
                            ''
                        )}
                        {getMarkInput({ others, size, value: toThousands ? showValue : value, value1: format ? value : '' })}
                        {hideActionButton ? (
                            ''
                        ) : (
                            <InputGroup.Button>
                                <div className={classnames('icon-group')}>
                                    <span
                                        id={id ? id + '_plus' : undefined}
                                        fieldid={fieldid ? fieldid + '_plus' : undefined}
                                        onMouseDown={(e: any) => handleStepMouseDown(e, 'add')}
                                        onClick={e => e.stopPropagation()}
                                        onMouseLeave={() => clear()}
                                        onMouseUp={() => clear()}
                                        className={plusClass}
                                    >
                                        <span className='uf uf-arrow-up' />
                                    </span>
                                    <span
                                        id={id ? id + '_minus' : undefined}
                                        fieldid={fieldid ? fieldid + '_minus' : undefined}
                                        onMouseDown={(e: any) => handleStepMouseDown(e, 'reduce')}
                                        onClick={e => e.stopPropagation()}
                                        onMouseLeave={() => clear()}
                                        onMouseUp={() => clear()}
                                        className={minusClass}
                                    >
                                        <span className='uf uf-arrow-down' />
                                    </span>
                                </div>
                            </InputGroup.Button>
                        )}
                    </InputGroup>
                )}
                {addonAfter ? (
                    <div className={classnames(className && `${className}-addonAfter`, `${clsPrefix}-addonAfter`, addonClasses)}>
                        {addonAfter}
                    </div>
                ) : null}
            </>
        )
    }

    let {
        id, fieldid, disabled, readOnly, clsPrefix, iconStyle, style, rootClassName, size, requiredStyle,
        align, bordered, dir, showAmountInWords, inputWidth, showAmountLayout = 'vertical', onlyShowAmount, browser
    } = props

    // ConfigProvider的context配置
    bordered = bordered ?? context?.bordered ?? true
    const _align = align || (iconStyle === 'double' ? 'center' : undefined)
    size = setComponentSize(size, { defaultIsMd: true }) as SizeType
    let adapterNid = getNid(props)

    // 金额大写功能只在中文环境下生效
    let tag = showAmountInWords && getLangInfo(props.locale, i18n, 'inputNumber').lang === 'zh-cn'
    let amount = tag && convertToChineseCurrency(state.value)

    // 浏览态
    if (browser) return domInput(true)

    return (
        <div
            id={id ? id + '_input-number' : undefined}
            fieldid={fieldid ? fieldid + '_input-number' : undefined}
            className={classnames(
                `${clsPrefix}-out`,
                {
                    [`${clsPrefix}-out-${size}`]: size,
                    [`${clsPrefix}-out-rtl`]: dir === 'rtl',
                    [`${clsPrefix}-icon-${iconStyle}`]: true,
                    [`${clsPrefix}-out-amount`]: tag,
                    [`${clsPrefix}-out-normal`]: !disabled && !readOnly,
                    [`${clsPrefix}-out-amount-horizontal`]: showAmountLayout === 'horizontal',
                    [`${clsPrefix}-out-amount-vertical`]: showAmountLayout === 'vertical' && tag,
                    [`${clsPrefix}-required-style-normal`]: requiredStyle && !disabled && !readOnly,
                    [`${clsPrefix}-clickable`]: props.onLinkClick && readOnly && state.value,
                    ...setComponentClass({ clsPrefix, bordered, align: _align, requiredStyle }),
                },
                disabled && `${clsPrefix}-disabled`,
                readOnly && `${clsPrefix}-readonly`,
                rootClassName
            )}
            onClick={() => {
                props.onLinkClick && readOnly && state.value && props.onLinkClick(state.value)
            }}
            style={style}
            {...adapterNid}
        >
            {
                tag ? (
                    <>
                        <div className={`${clsPrefix}-amount-dom`} style={{ width: inputWidth || 'unset' }}>{domInput()}</div>
                        {amount && <div className={`${clsPrefix}-amount`}>{onlyShowAmount ? '' : '大写：'}{amount}</div>}
                    </>
                ) : (
                    domInput()
                )
            }
        </div>
    )
}

// 应用装饰器
const DecoratedInputNumber = WithConfigConsumer({ name: "input-number" })(
    WebUI({ name: 'input-number', defaultProps })(InputNumberFunc)
) as React.FC<InputNumberProps> & {
    InputNumberGroup: typeof InputNumberGroup
}

// 添加静态属性
DecoratedInputNumber.InputNumberGroup = InputNumberGroup

export default DecoratedInputNumber